// Pure helpers for the connections admin UI: they turn a connection kind's
// field spec into a POST /connections body, gate the source-only fields on
// the kind's capabilities and translate the health/grant state into the
// labels the page renders. No React, no fetch — the i18n `t` function is
// passed in by the caller so this module stays testable on its own.

// Builds the POST /connections body from a connection kind's field spec: plain
// fields go to `config`, secret fields to `secrets` (never echoed back by the
// API). Blank optional fields are omitted so the backend applies its defaults —
// except a field that declares a `default`: the form pre-fills it, so a blank
// one was cleared on purpose and is sent as "" (e.g. auth_scheme "" = raw token).
export const SECONDS_PER_MINUTE = 60;

const hasDefault = (field) => field.default !== null && field.default !== undefined;

/** The initial form values of a kind: every field that declares a default. */
export function defaultFieldValues(kind) {
  const values = {};
  (kind?.fields || []).forEach((field) => {
    if (hasDefault(field)) values[field.name] = String(field.default);
  });
  return values;
}

/** The translator is injected by the caller; without one the raw key is used. */
const translate = (t) => (typeof t === "function" ? t : (key) => key);

/** Whether a kind descriptor or a connection row carries the given capability.
 * Null-safe: an unknown kind has no capabilities. */
export function kindHasCapability(kindOrConnection, capability) {
  const capabilities = kindOrConnection?.capabilities;
  return Array.isArray(capabilities) && capabilities.includes(capability);
}

const CAPABILITY_HINT_KEYS = {
  source: "adminConnectors.capSourceHint",
  tool: "adminConnectors.capToolHint",
  sink: "adminConnectors.capSinkHint",
};

/** What a capability does, in plain words; an unknown capability shows its raw name. */
export function capabilityHint(capability, t) {
  const key = CAPABILITY_HINT_KEYS[capability];
  return key ? translate(t)(key) : String(capability || "");
}

/** The form label of a kind field: the label the kind declares, else its name. */
export function fieldLabel(field) {
  return (field && (field.label || field.name)) || "";
}

/** Builds the POST /connections body. `slug` is only sent when it is not
 * blank (the API derives one otherwise); `collection_id` and
 * `schedule_seconds` only exist for source kinds — the API rejects them for
 * every other capability. */
export function buildConnectionBody({ name, slug, kind, database, collectionId, scheduleMinutes, values }) {
  const config = {};
  const secrets = {};
  const fieldValues = values || {};
  (kind?.fields || []).forEach((field) => {
    const value = String(fieldValues[field.name] || "").trim();
    if (!value && !(hasDefault(field) && !field.secret)) return;
    (field.secret ? secrets : config)[field.name] = value;
  });
  const body = {
    name: String(name || "").trim(),
    kind: kind?.kind || null,
    database: database || null,
    config,
    secrets,
    enabled: true,
  };
  const trimmedSlug = typeof slug === "string" ? slug.trim() : "";
  if (trimmedSlug) body.slug = trimmedSlug;
  if (kindHasCapability(kind, "source")) {
    body.collection_id = String(collectionId || "").trim() || null;
    const minutes = Number(scheduleMinutes) || 0;
    body.schedule_seconds = Math.max(0, Math.round(minutes * SECONDS_PER_MINUTE));
  }
  return body;
}

/** The required kind fields the user has not filled in yet. */
export function missingRequired(kind, values) {
  return (kind?.fields || [])
    .filter((f) => f.required && !(values[f.name] || "").trim())
    .map((f) => f.name);
}

/** One-line summary of the last sync run ("+2 ~1 -0 =5"). */
export function summarizeRun(summary) {
  if (!summary || summary.added === undefined) return "";
  const parts = [`+${summary.added}`, `~${summary.updated}`, `-${summary.deleted}`, `=${summary.unchanged}`];
  return summary.failed ? `${parts.join(" ")} · ${summary.failed} failed` : parts.join(" ");
}

/** The PUT /connections/{id}/grants/{capability} body. */
export function buildGrantBody({ enabled, roles, agentIds, requiresApproval }) {
  return {
    enabled,
    allowed_roles: [...(roles || [])],
    allowed_agent_ids: [...(agentIds || [])],
    requires_approval: requiresApproval,
  };
}

/** Why a grant cannot be saved yet, as a translated message — or null when it
 * can. `limit` is accepted for the future server-side cap; the server stays
 * the authority on how many roles may be granted. */
export function grantProblem(grant, t, limit) {
  const roles = grant?.roles || [];
  if (!roles.length) return translate(t)("adminConnectors.rolesRequired");
  return null;
}

/** The health badge of a connection. A failed probe is always a danger tone
 * and the server's detail is shown by the page, never inside the label. */
export function summarizeHealth(health, t) {
  const label = translate(t);
  if (health && health.ok === true) {
    return { tone: "success", label: label("adminConnectors.healthHealthy") };
  }
  if (health && health.ok === false) {
    return { tone: "danger", label: label("adminConnectors.healthUnhealthy") };
  }
  return { tone: "neutral", label: label("adminConnectors.healthNotTested") };
}
